import { useState } from "react";

import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import { OptionListProps } from "@/types/LocationFromType";
import { STCountryArray, STCountryObj } from "@/types/QuestionPayloadType";
import {
  findDuplicatesByProperty,
  findFCDOCountries,
} from "@/utils/question-location-to.utils";
import { checkCurentLocation } from "@/utils/travel-date.utils";

import { useCountryInfo } from "../provider/country-infor-provider";
import { useLocationTo } from "../provider/location-to-provider";

type UseSingleTripLocationHandlersProps = {
  fromCountryCode: string | undefined;
  setToLocationCountryDetails: (text?: STCountryArray | undefined) => void;
  modifyPayloadToLocation: (
    fromCountryCode: string | undefined,
    countryCode: string | undefined,
    stateCode?: string,
    countryDetailsArray?: STCountryArray
  ) => void;
  setSelectedState: (code: string) => void;
};

export function useSingleTripLocationHandlers({
  fromCountryCode,
  setToLocationCountryDetails,
  modifyPayloadToLocation,
  setSelectedState,
}: UseSingleTripLocationHandlersProps) {
  const [selectedSTCountry, setSelectedSTCountry] = useState<
    OptionListProps[] | undefined
  >(undefined);
  const [showSameCountryModal, setShowSameCountryModal] = useState(false);
  const [sameCountrySelected, setSameCountrySelected] = useState<
    OptionListProps[] | undefined
  >(undefined);
  const [isFCDOCountrySelected, setIsFCDOCountrySelected] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { current } = useWizard();
  const { restrictedCountry } = useCountryInfo();
  const { setSelectedFCDOCountryCode } = useLocationTo();

  // Handles the TagSearchField value change
  const tagSearchFieldOnChange = (selectedCountries: OptionListProps[]) => {
    let countryCodes = "";
    const countryDetailsArray: STCountryArray = [];

    if (selectedCountries && selectedCountries.length > 0) {
      const lastCountryCode =
        selectedCountries[selectedCountries.length - 1].parentCode;
      const isFCDOCountry = findFCDOCountries(
        lastCountryCode,
        restrictedCountry
      );
      if (!isFCDOCountry) {
        selectedCountries.forEach((country, index) => {
          countryCodes += `${country.parentCode}${
            index !== selectedCountries.length - 1 ? "," : ""
          }`;
          const countryDetail: STCountryObj = {
            attributes: {
              countryCode: country.parentCode,
              countryName: country.parentName,
            },
          };
          countryDetailsArray.push(countryDetail);
        });

        setToLocationCountryDetails(countryDetailsArray);
        modifyPayloadToLocation(
          fromCountryCode,
          countryCodes,
          undefined,
          countryDetailsArray
        );
        setSelectedState(countryCodes);
      } else {
        selectedCountries.splice(-1, 1);
        setSelectedSTCountry(selectedCountries as []);
        setSelectedFCDOCountryCode(lastCountryCode);
        setIsFCDOCountrySelected(true);
      }
    } else {
      modifyPayloadToLocation(undefined, undefined, undefined);
      setSelectedState("");
    }
  };

  // Handles the case when the same country is selected
  const handleSameCountrySelection = (
    newValues: OptionListProps[],
    lastCountry: OptionListProps
  ) => {
    if (newValues.length > 1) {
      newValues.splice(-1, 1);
      setSelectedSTCountry(newValues as []);
    } else {
      setSelectedSTCountry([]);
    }
    setShowSameCountryModal(true);
    setSameCountrySelected([lastCountry]);
  };

  // Handles the search country logic
  const handleSearchCountry = (values: OptionListProps[]) => {
    const newValues = values.slice();
    const lastCountry = newValues[newValues.length - 1];

    if (newValues.length === 0 || typeof lastCountry === "string") {
      setSelectedSTCountry([]);
      setSelectedState("");
      return;
    }

    const duplicateCountries = findDuplicatesByProperty(
      newValues,
      "parentCode"
    );
    if (duplicateCountries.length > 0) {
      setIsModalOpen(true);
      return;
    }

    const selectedCountryCode = lastCountry ? lastCountry.parentCode : "";
    const isDeleting =
      selectedSTCountry && selectedSTCountry.length > newValues.length;
    const isSameCountry = checkCurentLocation(
      fromCountryCode ?? (current.brandSpecificParams?.fromCountry as string),
      selectedCountryCode ?? ""
    );

    if (!isDeleting && isSameCountry) {
      handleSameCountrySelection(newValues, lastCountry);
    } else {
      setShowSameCountryModal(false);
      tagSearchFieldOnChange(newValues);
    }
  };

  return {
    selectedSTCountry,
    setSelectedSTCountry,
    showSameCountryModal,
    setShowSameCountryModal,
    sameCountrySelected,
    setSameCountrySelected,
    isFCDOCountrySelected,
    setIsFCDOCountrySelected,
    isModalOpen,
    setIsModalOpen,
    tagSearchFieldOnChange,
    handleSameCountrySelection,
    handleSearchCountry,
  };
}
